import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { type FormEvent, useState } from "react";

import { ApiError } from "../../api/errors";
import type {
  CandidateExposure,
  SchedulingCandidate,
  SchedulingCandidatesResponse
} from "../../api/statelessClient";
import { getProjectRepository } from "../../data/projectRepository";
import type {
  InterventionPlan,
  ResourceRoomProjectDocumentV2
} from "../../storage/resourceRoomDocument";
import { CompactMultiSelect } from "../shared/CompactMultiSelect";
import { ResourceRoomDialog } from "../shared/ResourceRoomDialog";
import { ResourceRoomProjectHeader } from "../shared/ResourceRoomProjectHeader";
import {
  demoInterventionCourseNames,
  demoRoomNames,
  demoStudentProfiles
} from "../shared/resourceRoomDemoData";
import "../shared/resource-room-workspace.css";
import { CandidatePicker } from "./CandidatePicker";

const statusLabels = {
  PENDING: "待排课",
  SCHEDULED: "已排课",
  NEEDS_CONFIRMATION: "需确认"
} as const;

interface PlanFieldValues {
  lesson_type: InterventionPlan["lesson_type"];
  course_id: string;
  student_ids: string[];
  weekly_sessions: number;
  consecutive_periods: number;
  week_pattern: InterventionPlan["week_pattern"];
  main_teacher_id: string;
  assistant_teacher_ids: string[];
  room_id: string;
}

interface Feedback {
  kind: "success" | "error";
  text: string;
}

function fieldsFromPlan(plan: InterventionPlan): PlanFieldValues {
  return {
    lesson_type: plan.lesson_type,
    course_id: plan.course_id,
    student_ids: plan.student_ids,
    weekly_sessions: plan.weekly_sessions,
    consecutive_periods: plan.consecutive_periods,
    week_pattern: plan.week_pattern,
    main_teacher_id: plan.main_teacher_id,
    assistant_teacher_ids: plan.assistant_teacher_ids,
    room_id: plan.room_id
  };
}

function planPayload(planId: string, values: PlanFieldValues): Record<string, unknown> {
  return { plan_id: planId, ...values };
}

function proposedPlan(plan: InterventionPlan, values: PlanFieldValues): InterventionPlan {
  return {
    ...plan,
    ...values,
    status: "PENDING",
    confirmed_protection_fingerprint: null
  };
}

function PlanForm({
  document,
  busy,
  initialPlan,
  preset,
  submitLabel,
  onSubmit
}: {
  document: ResourceRoomProjectDocumentV2;
  busy: boolean;
  initialPlan?: InterventionPlan;
  preset?: PlanFieldValues;
  submitLabel: string;
  onSubmit: (values: PlanFieldValues) => void;
}): React.JSX.Element {
  const defaults = initialPlan ? fieldsFromPlan(initialPlan) : preset;
  const [lessonType, setLessonType] = useState<"INDIVIDUAL" | "GROUP">(
    defaults?.lesson_type ?? "INDIVIDUAL"
  );
  const [courseId, setCourseId] = useState(
    defaults?.course_id ?? document.entities.intervention_courses[0]?.id ?? ""
  );
  const [students, setStudents] = useState<string[]>(defaults?.student_ids ?? []);
  const [weekly, setWeekly] = useState(defaults?.weekly_sessions ?? 1);
  const [consecutive, setConsecutive] = useState(
    defaults?.consecutive_periods ?? 1
  );
  const [weekPattern, setWeekPattern] = useState<"EVERY" | "ODD" | "EVEN">(
    defaults?.week_pattern ?? "EVERY"
  );
  const [mainTeacher, setMainTeacher] = useState(
    defaults?.main_teacher_id ?? ""
  );
  const [assistants, setAssistants] = useState<string[]>(
    defaults?.assistant_teacher_ids ?? []
  );
  const [roomId, setRoomId] = useState(
    defaults?.room_id ?? document.entities.rooms[0]?.id ?? ""
  );
  const studentOptions = document.entities.students
    .filter((student) => (student.grade_label && student.class_name) || students.includes(student.id))
    .map((student) => ({
      id: student.id,
      label: student.display_name,
      description: `${student.grade_label ?? "年级待补充"}${student.class_name ?? ""}`
    }));
  function submit(event: FormEvent): void {
    event.preventDefault();
    onSubmit({
      lesson_type: lessonType,
      course_id: courseId,
      student_ids: students,
      weekly_sessions: weekly,
      consecutive_periods: consecutive,
      week_pattern: weekPattern,
      main_teacher_id: mainTeacher,
      assistant_teacher_ids: assistants,
      room_id: roomId
    });
  }
  return (
    <form className="resource-form-grid" onSubmit={submit}>
      <label className="field">
        <span>课型</span>
        <select value={lessonType} onChange={(event) => {
          const next = event.target.value as typeof lessonType;
          setLessonType(next);
          if (next === "INDIVIDUAL" && students.length > 1) setStudents(students.slice(0, 1));
        }}>
          <option value="INDIVIDUAL">个训</option>
          <option value="GROUP">小组</option>
        </select>
      </label>
      <label className="field">
        <span>干预课程</span>
        <select required value={courseId} onChange={(event) => setCourseId(event.target.value)}>
          <option value="">请选择</option>
          {document.entities.intervention_courses.map((item) => <option key={item.id} value={item.id}>{item.name}</option>)}
        </select>
      </label>
      <div className="span-all">
        <CompactMultiSelect
          label="选择学生"
          value={students}
          options={studentOptions}
          max={lessonType === "INDIVIDUAL" ? 1 : undefined}
          searchable
          placeholder={lessonType === "INDIVIDUAL" ? "请选择 1 名学生" : "请选择至少 2 名学生"}
          onChange={setStudents}
        />
      </div>
      <label className="field">
        <span>每周次数</span>
        <input type="number" min="1" value={weekly} disabled={Boolean(initialPlan)} onChange={(event) => setWeekly(Number(event.target.value))} />
        {initialPlan ? <small className="field-hint">请在计划列表使用“增加一次”或“减少一次”。</small> : null}
      </label>
      <label className="field">
        <span>每次连续节数</span>
        <select value={consecutive} onChange={(event) => setConsecutive(Number(event.target.value))}>
          {[1, 2, 3, 4].map((value) => <option key={value} value={value}>{value} 节</option>)}
        </select>
      </label>
      <label className="field">
        <span>周次</span>
        <select value={weekPattern} onChange={(event) => setWeekPattern(event.target.value as typeof weekPattern)}>
          <option value="EVERY">每周</option><option value="ODD">单周</option><option value="EVEN">双周</option>
        </select>
      </label>
      <label className="field">
        <span>主教</span>
        <select required value={mainTeacher} onChange={(event) => {
          setMainTeacher(event.target.value);
          setAssistants(assistants.filter((id) => id !== event.target.value));
        }}>
          <option value="">请选择</option>
          {document.entities.teachers.map((item) => <option key={item.id} value={item.id}>{item.name}</option>)}
        </select>
      </label>
      <div className="span-all">
        <CompactMultiSelect
          label="选择助教（0—3 名）"
          value={assistants}
          options={document.entities.teachers
            .filter((teacher) => teacher.id !== mainTeacher)
            .map((teacher) => ({ id: teacher.id, label: teacher.name }))}
          max={3}
          placeholder="不选则无助教"
          onChange={setAssistants}
        />
      </div>
      <label className="field">
        <span>具体教室</span>
        <select required value={roomId} onChange={(event) => setRoomId(event.target.value)}>
          <option value="">请选择</option>
          {document.entities.rooms.map((item) => <option key={item.id} value={item.id}>{item.name}</option>)}
        </select>
      </label>
      <div className="span-all">
        <button className="primary-button" type="submit" disabled={busy || !courseId || !students.length || !mainTeacher || !roomId}>{submitLabel}</button>
      </div>
    </form>
  );
}

interface CandidateState {
  planId: string;
  fixedSessionIds: string[];
  excluded: string[];
  proposedPlan: InterventionPlan | null;
  result: SchedulingCandidatesResponse;
}

interface SessionChoiceState {
  mode: "REDUCE" | "ADJUST";
  planId: string;
}

export function InterventionPlansPage({ projectId }: { projectId: string }): React.JSX.Element {
  const queryClient = useQueryClient();
  const [activeTab, setActiveTab] = useState<"PLANS" | "SCHEDULED">("PLANS");
  const [createOpen, setCreateOpen] = useState(false);
  const [statusFilter, setStatusFilter] = useState("ALL");
  const [studentFilter, setStudentFilter] = useState("ALL");
  const [courseFilter, setCourseFilter] = useState("ALL");
  const [teacherFilter, setTeacherFilter] = useState("ALL");
  const [feedback, setFeedback] = useState<Feedback | null>(null);
  const [candidateState, setCandidateState] = useState<CandidateState | null>(null);
  const [protectedPrompt, setProtectedPrompt] = useState<CandidateExposure[] | null>(null);
  const [protectedConfirmed, setProtectedConfirmed] = useState(false);
  const [editingPlanId, setEditingPlanId] = useState<string | null>(null);
  const [sessionChoice, setSessionChoice] = useState<SessionChoiceState | null>(null);
  const [formPreset, setFormPreset] = useState<{
    key: string;
    values: PlanFieldValues;
  } | null>(null);
  const documentQuery = useQuery({
    queryKey: ["resource-room-document", projectId],
    queryFn: async () => (await getProjectRepository()).readDocument(projectId)
  });
  const refresh = async (): Promise<void> => {
    await queryClient.invalidateQueries({
      predicate: (item) => item.queryKey.includes(projectId)
    });
  };
  const commandMutation = useMutation({
    mutationFn: async ({ type, payload }: { type: string; payload: Record<string, unknown> }) =>
      (await getProjectRepository()).execute(projectId, type, payload),
    onSuccess: async (_result, variables) => {
      setFeedback({ kind: "success", text: "计划已保存" });
      setSessionChoice(null);
      if (variables.type === "CREATE_PLAN") {
        setCreateOpen(false);
        setFormPreset(null);
      }
      await refresh();
    },
    onError: (error: unknown) => setFeedback({
      kind: "error",
      text: error instanceof Error ? error.message : "操作失败"
    })
  });
  const searchMutation = useMutation({
    mutationFn: async ({
      planId,
      fixedSessionIds,
      excluded,
      proposed
    }: {
      planId: string;
      fixedSessionIds: string[];
      excluded: string[];
      proposed: InterventionPlan | null;
    }) => (await getProjectRepository()).searchCandidates(projectId, {
      plan_id: planId,
      fixed_session_ids: fixedSessionIds,
      excluded_candidates: excluded,
      ...(proposed ? { proposed_plan: proposed } : {})
    }),
    onSuccess: (result, variables) => {
      setCandidateState({
        planId: variables.planId,
        fixedSessionIds: variables.fixedSessionIds,
        proposedPlan: variables.proposed,
        excluded: [
          ...variables.excluded,
          ...result.candidates.map((item) => item.signature)
        ],
        result
      });
      setSessionChoice(null);
      setFeedback(null);
    },
    onError: (error: unknown) => setFeedback({
      kind: "error",
      text: error instanceof Error ? error.message : "候选计算失败"
    })
  });
  const editMutation = useMutation({
    mutationFn: async ({ plan, values }: { plan: InterventionPlan; values: PlanFieldValues }) => {
      const repository = await getProjectRepository();
      try {
        await repository.execute(projectId, "UPDATE_PLAN", planPayload(plan.id, values));
        return { kind: "UPDATED" as const };
      } catch (error) {
        if (!(error instanceof ApiError) || error.code !== "HARD_CONFLICT") throw error;
        const proposed = proposedPlan(plan, values);
        const result = await repository.searchCandidates(projectId, {
          plan_id: plan.id,
          proposed_plan: proposed,
          fixed_session_ids: [],
          excluded_candidates: []
        });
        return { kind: "CANDIDATES" as const, plan, proposed, result };
      }
    },
    onSuccess: async (result) => {
      setEditingPlanId(null);
      if (result.kind === "UPDATED") {
        setFeedback({ kind: "success", text: "计划资料已保存，已重新检查全部时间块" });
        await refresh();
        return;
      }
      setCandidateState({
        planId: result.plan.id,
        fixedSessionIds: [],
        proposedPlan: result.proposed,
        excluded: result.result.candidates.map((item) => item.signature),
        result: result.result
      });
      setFeedback(null);
    },
    onError: (error: unknown) => setFeedback({
      kind: "error",
      text: error instanceof Error ? error.message : "计划资料保存失败"
    })
  });
  const commitMutation = useMutation({
    mutationFn: async ({ candidate, confirmProtected }: { candidate: SchedulingCandidate; confirmProtected: boolean }) => {
      if (!candidateState) throw new Error("候选方案已失效");
      return (await getProjectRepository()).commitCandidate(projectId, {
        plan_id: candidateState.planId,
        fixed_session_ids: candidateState.fixedSessionIds,
        condition_fingerprint: candidateState.result.condition_fingerprint,
        candidate,
        confirm_protected: confirmProtected,
        ...(candidateState.proposedPlan
          ? { proposed_plan: candidateState.proposedPlan }
          : {})
      });
    },
    onSuccess: async () => {
      setCandidateState(null);
      setFeedback({ kind: "success", text: "方案已保存，计划进入已排课状态" });
      await refresh();
    },
    onError: (error: unknown) => setFeedback({
      kind: "error",
      text: error instanceof Error ? error.message : "方案保存失败"
    })
  });
  const globalScheduleMutation = useMutation({
    mutationFn: async (confirmProtected: boolean) =>
      (await getProjectRepository()).scheduleGlobal(projectId, confirmProtected),
    onSuccess: async () => {
      setProtectedPrompt(null);
      setProtectedConfirmed(false);
      setActiveTab("SCHEDULED");
      setFeedback({
        kind: "success",
        text: "全部计划已排课"
      });
      await refresh();
    },
    onError: (error: unknown) => {
      if (error instanceof ApiError && error.code === "PROTECTED_CONFIRMATION_REQUIRED") {
        const details = error.details as { exposures?: CandidateExposure[] } | null;
        setProtectedPrompt(details?.exposures ?? []);
        setProtectedConfirmed(false);
        setFeedback(null);
        return;
      }
      setFeedback({
        kind: "error",
        text: error instanceof Error ? error.message : "排课失败"
      });
    }
  });
  const planActionsBusy =
    commandMutation.isPending ||
    searchMutation.isPending ||
    editMutation.isPending ||
    commitMutation.isPending ||
    globalScheduleMutation.isPending;

  if (documentQuery.isLoading) {
    return <main className="page-shell"><p className="state-message">正在读取干预计划…</p></main>;
  }
  if (!documentQuery.data) {
    return <main className="page-shell"><p className="error-message">干预计划无法读取。</p></main>;
  }
  const document = documentQuery.data;
  const pendingPlans = document.entities.intervention_plans.filter(
    (plan) => plan.status === "PENDING"
  );
  const pendingPlanCount = pendingPlans.length;
  const scheduledPlanCount = document.entities.intervention_plans.filter(
    (plan) => plan.status === "SCHEDULED"
  ).length;
  const needsConfirmationCount = document.entities.intervention_plans.filter(
    (plan) => plan.status === "NEEDS_CONFIRMATION"
  ).length;
  const studentIds = new Set(document.entities.students.map((item) => item.id));
  const courseIds = new Set(
    document.entities.intervention_courses.map((item) => item.id)
  );
  const teacherIds = new Set(document.entities.teachers.map((item) => item.id));
  const roomIds = new Set(document.entities.rooms.map((item) => item.id));
  const activeOriginalCourseIds = new Set(
    document.entities.student_original_lessons.map((item) => item.original_course_id)
  );
  const globalReadinessIssue = pendingPlanCount === 0
    ? "当前没有待排计划。"
    : !document.entities.time_slots.length
      ? "请先导入学生原班课表。"
      : document.entities.original_courses.some(
          (course) => activeOriginalCourseIds.has(course.id) && !course.nature
        )
        ? "请先设置全部已识别原班课程的课程性质。"
        : !document.entities.teachers.length || pendingPlans.some((plan) =>
              !courseIds.has(plan.course_id) ||
              !roomIds.has(plan.room_id) ||
              !teacherIds.has(plan.main_teacher_id) ||
              plan.assistant_teacher_ids.some((id) => !teacherIds.has(id)) ||
              plan.student_ids.some((id) => !studentIds.has(id))
            )
          ? "请先补齐计划引用的学生、课程、教师和教室资料。"
          : null;
  const courseNames = new Map(
    document.entities.intervention_courses.map((item) => [item.id, item.name])
  );
  const studentNames = new Map(
    document.entities.students.map((item) => [item.id, item.display_name])
  );
  const teacherNames = new Map(
    document.entities.teachers.map((item) => [item.id, item.name])
  );
  const roomNames = new Map(document.entities.rooms.map((item) => [item.id, item.name]));
  const slotNames = new Map(
    document.entities.time_slots.map((item) => [item.id, item.raw_label])
  );
  const sessionsFor = (planId: string) =>
    document.entities.intervention_sessions
      .filter((item) => item.plan_id === planId)
      .sort((left, right) => left.session_index - right.session_index);
  const plans = document.entities.intervention_plans.filter((plan) => {
    return (
      (statusFilter === "ALL" || plan.status === statusFilter) &&
      (studentFilter === "ALL" || plan.student_ids.includes(studentFilter)) &&
      (courseFilter === "ALL" || plan.course_id === courseFilter) &&
      (teacherFilter === "ALL" ||
        plan.main_teacher_id === teacherFilter ||
        plan.assistant_teacher_ids.includes(teacherFilter))
    );
  });
  const editingPlan = document.entities.intervention_plans.find(
    (item) => item.id === editingPlanId
  );
  const choicePlan = document.entities.intervention_plans.find(
    (item) => item.id === sessionChoice?.planId
  );
  const choiceSessions = choicePlan ? sessionsFor(choicePlan.id) : [];
  const scheduledPlans = plans.filter((plan) => sessionsFor(plan.id).length > 0);
  const scheduledTabCount = document.entities.intervention_plans.filter(
    (plan) => sessionsFor(plan.id).length > 0
  ).length;
  const sessionLabel = (session: (typeof document.entities.intervention_sessions)[number]) =>
    `星期${"一二三四五"[session.weekday - 1]} · ${session.time_slot_ids
      .map((id) => slotNames.get(id))
      .join(" + ")}`;

  function demoPlanValues(kind: "GROUP" | "INDIVIDUAL"): PlanFieldValues | null {
    const mainTeacher = document.entities.teachers[0];
    const eligible = document.entities.students.filter(
      (student) => student.grade_label && student.class_name
    );
    const orderedDemoStudents = demoStudentProfiles.flatMap((profile) => {
      const student = eligible.find((item) => item.import_key === profile.importKey);
      return student ? [student] : [];
    });
    const orderedStudents = orderedDemoStudents.length ? orderedDemoStudents : eligible;
    const students = kind === "GROUP" ? orderedStudents.slice(0, 3) : orderedStudents.slice(0, 1);
    const courseName = kind === "GROUP" ? demoInterventionCourseNames[0] : demoInterventionCourseNames[1];
    const course =
      document.entities.intervention_courses.find((item) => item.name === courseName) ??
      document.entities.intervention_courses[0];
    const roomName = kind === "GROUP" ? demoRoomNames[0] : demoRoomNames[1];
    const room = document.entities.rooms.find((item) => item.name === roomName) ?? document.entities.rooms[0];
    if (!mainTeacher || !course || !room || students.length < (kind === "GROUP" ? 2 : 1)) return null;
    const assistant = document.entities.teachers.find((item) => item.id !== mainTeacher.id);
    return {
      lesson_type: kind,
      course_id: course.id,
      student_ids: students.map((item) => item.id),
      weekly_sessions: 2,
      consecutive_periods: kind === "GROUP" ? 1 : 2,
      week_pattern: kind === "GROUP" ? "EVERY" : "ODD",
      main_teacher_id: mainTeacher.id,
      assistant_teacher_ids: kind === "GROUP" && assistant ? [assistant.id] : [],
      room_id: room.id
    };
  }

  const groupPreset = demoPlanValues("GROUP");
  const individualPreset = demoPlanValues("INDIVIDUAL");

  function fillPlanPreset(values: PlanFieldValues): void {
    setFormPreset({ key: crypto.randomUUID(), values });
    setCreateOpen(true);
    setFeedback(null);
  }

  function addOneSession(plan: InterventionPlan): void {
    const sessions = sessionsFor(plan.id);
    const next = proposedPlan(plan, {
      ...fieldsFromPlan(plan),
      weekly_sessions: plan.weekly_sessions + 1
    });
    searchMutation.mutate({
      planId: plan.id,
      fixedSessionIds: sessions.map((item) => item.id),
      excluded: [],
      proposed: next
    });
  }

  return (
    <main className="page-shell page-shell-wide">
      <ResourceRoomProjectHeader
        projectId={projectId}
        eyebrow="阶段 3 · 干预排课"
        title="干预计划"
        actions={(
          <button
            className="primary-button"
            type="button"
            disabled={planActionsBusy}
            onClick={() => {
              setFormPreset(null);
              setCreateOpen(true);
            }}
          >
            新建计划
          </button>
        )}
      />

      <nav className="resource-tabs resource-plan-tabs" aria-label="干预排课内容">
        <button
          className={activeTab === "PLANS" ? "active" : ""}
          type="button"
          onClick={() => setActiveTab("PLANS")}
        >
          干预计划 <span>{document.entities.intervention_plans.length}</span>
        </button>
        <button
          className={activeTab === "SCHEDULED" ? "active" : ""}
          type="button"
          onClick={() => {
            setStatusFilter("ALL");
            setActiveTab("SCHEDULED");
          }}
        >
          已排课程 <span>{scheduledTabCount}</span>
        </button>
      </nav>

      {feedback ? (
        <p className={feedback.kind === "success" ? "resource-success" : "resource-errors"} role={feedback.kind === "error" ? "alert" : "status"}>
          {feedback.text}
        </p>
      ) : null}

      {candidateState ? (
        <CandidatePicker
          document={document}
          plan={candidateState.proposedPlan ?? document.entities.intervention_plans.find(
            (plan) => plan.id === candidateState.planId
          )!}
          result={candidateState.result}
          busy={commitMutation.isPending || searchMutation.isPending}
          onChoose={(candidate, confirmProtected) => commitMutation.mutate({ candidate, confirmProtected })}
          onMore={() => searchMutation.mutate({
            planId: candidateState.planId,
            fixedSessionIds: candidateState.fixedSessionIds,
            excluded: candidateState.excluded,
            proposed: candidateState.proposedPlan
          })}
          onClose={() => setCandidateState(null)}
        />
      ) : null}

      <section className="resource-panel resource-plan-list">
        {activeTab === "PLANS" ? (
          <div className="resource-plan-toolbar">
            <div className="resource-global-counts" aria-label="计划状态">
              <span><strong>{pendingPlanCount}</strong> 待排</span>
              <span><strong>{scheduledPlanCount}</strong> 已排</span>
              {needsConfirmationCount ? <span><strong>{needsConfirmationCount}</strong> 需确认</span> : null}
            </div>
            <div className="resource-plan-schedule-action">
              <button
                className="primary-button"
                type="button"
                disabled={Boolean(globalReadinessIssue) || planActionsBusy}
                onClick={() => globalScheduleMutation.mutate(false)}
              >
                {globalScheduleMutation.isPending ? "正在排课…" : "一键排课"}
              </button>
              {globalReadinessIssue ? <small>{globalReadinessIssue}</small> : null}
            </div>
          </div>
        ) : null}
        <div className="resource-batch-bar" aria-label="计划筛选">
          {activeTab === "PLANS" ? <label className="field"><span>状态</span><select value={statusFilter} onChange={(event) => setStatusFilter(event.target.value)}><option value="ALL">全部</option><option value="PENDING">待排课</option><option value="SCHEDULED">已排课</option><option value="NEEDS_CONFIRMATION">需确认</option></select></label> : null}
          <label className="field"><span>学生</span><select value={studentFilter} onChange={(event) => setStudentFilter(event.target.value)}><option value="ALL">全部</option>{document.entities.students.map((item) => <option key={item.id} value={item.id}>{item.display_name} · {item.grade_label}{item.class_name}</option>)}</select></label>
          <label className="field"><span>课程</span><select value={courseFilter} onChange={(event) => setCourseFilter(event.target.value)}><option value="ALL">全部</option>{document.entities.intervention_courses.map((item) => <option key={item.id} value={item.id}>{item.name}</option>)}</select></label>
          <label className="field"><span>教师</span><select value={teacherFilter} onChange={(event) => setTeacherFilter(event.target.value)}><option value="ALL">全部</option>{document.entities.teachers.map((item) => <option key={item.id} value={item.id}>{item.name}</option>)}</select></label>
          {activeTab === "SCHEDULED" && scheduledTabCount > 0 ? (
            <a className="primary-button resource-batch-action" href={`/projects/${projectId}/timetables`}>
              查看课表
            </a>
          ) : null}
        </div>
        <div className="resource-table-scroll">
          {activeTab === "PLANS" ? <table className="data-table resource-plan-table">
            <thead><tr><th>课程</th><th>学生</th><th>安排要求</th><th>教师与教室</th><th>状态</th><th>操作</th></tr></thead>
            <tbody>
              {plans.map((plan) => {
                const sessions = sessionsFor(plan.id);
                const statusClass = plan.status === "SCHEDULED" ? "scheduled" : plan.status === "PENDING" ? "pending" : "confirmation";
                return (
                  <tr key={plan.id}>
                    <td><strong>{courseNames.get(plan.course_id)}</strong><br />{plan.lesson_type === "INDIVIDUAL" ? "个训" : "小组"} · {plan.week_pattern === "EVERY" ? "每周" : plan.week_pattern === "ODD" ? "单周" : "双周"}</td>
                    <td>{plan.student_ids.map((id) => studentNames.get(id)).join("、")}</td>
                    <td>每周 {plan.weekly_sessions} 次 · 连续 {plan.consecutive_periods} 节</td>
                    <td>主教：{teacherNames.get(plan.main_teacher_id)}<br />助教：{plan.assistant_teacher_ids.map((id) => teacherNames.get(id)).join("、") || "无"}<br />{roomNames.get(plan.room_id)}</td>
                    <td><span className={`resource-status ${statusClass}`}>{statusLabels[plan.status]}</span></td>
                    <td>
                      <div className="resource-actions">
                        <button className="text-button" type="button" disabled={planActionsBusy} onClick={() => setEditingPlanId(plan.id)}>编辑</button>
                        {!sessions.length ? <button className="text-button" type="button" disabled={planActionsBusy} onClick={() => searchMutation.mutate({ planId: plan.id, fixedSessionIds: [], excluded: [], proposed: null })}>单项排课</button> : <button className="text-button" type="button" onClick={() => setActiveTab("SCHEDULED")}>查看课程</button>}
                        <button className="text-button danger" type="button" disabled={planActionsBusy} onClick={() => {
                          if (window.confirm("确认删除该计划及已排课程？")) {
                            commandMutation.mutate({ type: "DELETE_PLAN", payload: { plan_id: plan.id } });
                          }
                        }}>删除</button>
                      </div>
                    </td>
                  </tr>
                );
              })}
              {!plans.length ? <tr><td colSpan={6}>暂无计划</td></tr> : null}
            </tbody>
          </table> : <table className="data-table resource-scheduled-table">
            <thead><tr><th>课程</th><th>学生</th><th>上课时间</th><th>教师与教室</th><th>状态</th><th>操作</th></tr></thead>
            <tbody>
              {scheduledPlans.map((plan) => {
                const sessions = sessionsFor(plan.id);
                const statusClass = plan.status === "SCHEDULED" ? "scheduled" : "confirmation";
                return <tr key={plan.id}>
                  <td><strong>{courseNames.get(plan.course_id)}</strong><br />{plan.lesson_type === "INDIVIDUAL" ? "个训" : "小组"} · {plan.week_pattern === "EVERY" ? "每周" : plan.week_pattern === "ODD" ? "单周" : "双周"}<br />每周 {plan.weekly_sessions} 次 · 连续 {plan.consecutive_periods} 节</td>
                  <td>{plan.student_ids.map((id) => studentNames.get(id)).join("、")}</td>
                  <td>{sessions.map((session) => <div key={session.id}>{sessionLabel(session)}</div>)}</td>
                  <td>{teacherNames.get(plan.main_teacher_id)}{plan.assistant_teacher_ids.length ? ` + ${plan.assistant_teacher_ids.map((id) => teacherNames.get(id)).join("、")}` : ""}<br />{roomNames.get(plan.room_id)}</td>
                  <td><span className={`resource-status ${statusClass}`}>{statusLabels[plan.status]}</span></td>
                  <td><div className="resource-actions">
                    <button className="text-button" type="button" disabled={planActionsBusy} onClick={() => searchMutation.mutate({ planId: plan.id, fixedSessionIds: [], excluded: [], proposed: null })}>整项重排</button>
                    <button className="text-button" type="button" disabled={planActionsBusy} onClick={() => setSessionChoice({ mode: "ADJUST", planId: plan.id })}>调整时段</button>
                    {sessions.length === plan.weekly_sessions ? <button className="text-button" type="button" disabled={planActionsBusy} onClick={() => addOneSession(plan)}>增加一次</button> : null}
                    {plan.weekly_sessions > 1 && sessions.length === plan.weekly_sessions ? <button className="text-button" type="button" disabled={planActionsBusy} onClick={() => setSessionChoice({ mode: "REDUCE", planId: plan.id })}>减少一次</button> : null}
                    {plan.status === "NEEDS_CONFIRMATION" ? <button className="text-button" type="button" disabled={planActionsBusy} onClick={() => commandMutation.mutate({ type: "CONFIRM_PLAN_PROTECTION", payload: { plan_id: plan.id } })}>确认安排</button> : null}
                  </div></td>
                </tr>;
              })}
              {!scheduledPlans.length ? <tr><td colSpan={6}>暂无已排课程</td></tr> : null}
            </tbody>
          </table>}
        </div>
      </section>

      {createOpen ? <ResourceRoomDialog
        title="新建干预计划"
        description="个训选 1 名学生，小组选 2 名及以上。"
        wide
        busy={commandMutation.isPending}
        onClose={() => {
          setCreateOpen(false);
          setFormPreset(null);
        }}
      >
        <div className="resource-dialog-actions resource-plan-demo-actions">
          <button className="text-button" type="button" disabled={!groupPreset || planActionsBusy} onClick={() => groupPreset && fillPlanPreset(groupPreset)}>填入小组示例</button>
          <button className="text-button" type="button" disabled={!individualPreset || planActionsBusy} onClick={() => individualPreset && fillPlanPreset(individualPreset)}>填入个训示例</button>
        </div>
        <PlanForm
          key={formPreset?.key ?? "blank-plan"}
          document={document}
          busy={planActionsBusy}
          preset={formPreset?.values}
          submitLabel="保存计划"
          onSubmit={(values) => commandMutation.mutate({ type: "CREATE_PLAN", payload: { ...values } })}
        />
      </ResourceRoomDialog> : null}

      {editingPlan ? <ResourceRoomDialog
        title="编辑计划"
        description={`${courseNames.get(editingPlan.course_id)} · ${editingPlan.student_ids.map((id) => studentNames.get(id)).join("、")}`}
        wide
        busy={editMutation.isPending}
        onClose={() => setEditingPlanId(null)}
      >
        <PlanForm
          key={editingPlan.id}
          document={document}
          busy={editMutation.isPending}
          initialPlan={editingPlan}
          submitLabel="保存修改"
          onSubmit={(values) => editMutation.mutate({ plan: editingPlan, values })}
        />
      </ResourceRoomDialog> : null}

      {sessionChoice && choicePlan ? <ResourceRoomDialog
        title={sessionChoice.mode === "REDUCE" ? "选择要移除的时段" : "选择要调整的时段"}
        description={`${courseNames.get(choicePlan.course_id)} · 每周 ${choicePlan.weekly_sessions} 次`}
        busy={planActionsBusy}
        onClose={() => setSessionChoice(null)}
      >
        <div className="resource-session-choice-list">
          {choiceSessions.map((session) => <div className="resource-session-choice" key={session.id}>
            <strong>{sessionLabel(session)}</strong>
            <button className="secondary-button" type="button" disabled={planActionsBusy} aria-label={`${sessionChoice.mode === "REDUCE" ? "移除" : "调整"} ${sessionLabel(session)}`} onClick={() => {
              if (sessionChoice.mode === "REDUCE") {
                commandMutation.mutate({ type: "REDUCE_PLAN_SESSIONS", payload: { plan_id: choicePlan.id, new_weekly_sessions: choicePlan.weekly_sessions - 1, remove_session_ids: [session.id] } });
              } else {
                searchMutation.mutate({ planId: choicePlan.id, fixedSessionIds: choiceSessions.filter((item) => item.id !== session.id).map((item) => item.id), excluded: [], proposed: null });
              }
            }}>{sessionChoice.mode === "REDUCE" ? "移除" : "调整"}</button>
          </div>)}
        </div>
      </ResourceRoomDialog> : null}

      {protectedPrompt ? <ResourceRoomDialog
        title="确认抽离保护课程"
        description="下列安排会占用保护课程。"
        busy={globalScheduleMutation.isPending}
        onClose={() => setProtectedPrompt(null)}
      >
        <ul className="resource-protection-list">
          {protectedPrompt.map((exposure) => <li key={`${exposure.student_id}-${exposure.session_index}-${exposure.weekday}-${exposure.time_slot_id}`}>
            <strong>{studentNames.get(exposure.student_id)} · 星期{"" + "一二三四五"[exposure.weekday - 1]} · {slotNames.get(exposure.time_slot_id)}</strong>
            <span>{exposure.original_course_name ?? "保护课程"}</span>
          </li>)}
        </ul>
        <label className="resource-candidate-confirmation"><input type="checkbox" checked={protectedConfirmed} onChange={(event) => setProtectedConfirmed(event.target.checked)} /><span>已核对上述抽离安排</span></label>
        <div className="resource-candidate-actions">
          <button className="secondary-button" type="button" disabled={globalScheduleMutation.isPending} onClick={() => setProtectedPrompt(null)}>取消</button>
          <button className="primary-button" type="button" disabled={!protectedConfirmed || globalScheduleMutation.isPending} onClick={() => globalScheduleMutation.mutate(true)}>{globalScheduleMutation.isPending ? "正在排课…" : "确认并排课"}</button>
        </div>
      </ResourceRoomDialog> : null}
    </main>
  );
}
